import { logger } from '@/shared/lib/logger';

/**
 * 라우터 의존성 없이 네비게이션을 수행하는 유틸리티
 *
 * 순환 참조 방지를 위해 이벤트 기반으로 구현합니다.
 * - API 클라이언트에서 라우터를 직접 import하면 순환 참조 발생
 * - 대신 리스너 패턴을 사용하여 라우터 초기화 후 네비게이션 위임
 *
 * @note 라우터가 준비되지 않은 상태에서 navigateTo 호출 시
 *       window.location.href fallback 대신 경고만 출력합니다.
 *       이는 페이지 전체 리로드로 인한 상태 손실을 방지합니다.
 */
type NavigationListener = (path: string) => void;
type LocationGetter = () => string;

let navigationListener: NavigationListener | null = null;
let locationGetter: LocationGetter | null = null;
let isRouterReady = false;

/**
 * 네비게이션 리스너 및 위치 getter 등록/해제
 * AppProvider에서 라우터 초기화 후 호출됩니다.
 * null을 전달하면 리스너가 해제됩니다 (cleanup용).
 */
export function setNavigationListener(
  listener: NavigationListener | null,
  getLocation?: LocationGetter | null,
) {
  navigationListener = listener;
  locationGetter = getLocation ?? null;
  isRouterReady = listener !== null;
}

/**
 * 라우터 준비 상태 확인
 */
export function getIsRouterReady(): boolean {
  return isRouterReady;
}

/**
 * 현재 경로 가져오기
 * 라우터가 준비되지 않은 경우 fallback 값을 반환합니다.
 *
 * @param fallback - 라우터 미준비 시 반환할 기본값 (기본: '/login')
 */
export function getCurrentPath(fallback = '/login'): string {
  if (locationGetter && isRouterReady) {
    return locationGetter();
  }
  return fallback;
}

/**
 * 지정된 경로로 네비게이션
 * 리스너가 등록되지 않은 경우 (라우터 초기화 전) 경고만 출력하고 무시합니다.
 * 이는 API 응답 처리 중 컴포넌트 언마운트 시 발생할 수 있는 race condition을 방지합니다.
 */
export function navigateTo(path: string) {
  if (navigationListener && isRouterReady) {
    navigationListener(path);
  } else {
    // 라우터가 준비되지 않은 상태 - 경고만 출력
    // window.location.href fallback 제거: 페이지 리로드로 인한 상태 손실 방지
    logger.warn('Router not ready, navigation ignored', {
      action: 'navigation',
      metadata: { targetPath: path, isRouterReady },
    });
  }
}
